import { collectionUtils } from '../../../../../utils';
import {
  consumerAddressCombinations,
  consumerAddressMinimumFields,
  consumerNameCombinations,
  consumerNameMinimumsFields,
  checkAddressCompleteness,
} from '../../validators';
import { InputLayoutModel } from '../../common';
import * as yup from 'yup';

export const inputFieldValidator = yup.object<InputLayoutModel>({
  source: yup.string().oneOf(['Inline', 'Client']).required(),
  fieldName: yup
    .string()
    .label('Experian Header')
    .notRequired()
    .test(
      'InvalidChars',
      'Invalid characters in the header name',
      function validCharacters(currentField) {
        if (currentField?.length < 1) {
          return true;
          // return this.createError({
          //   path: this.path,
          //   message: 'Field Header cannot be empty.',
          // });
        }
        // console.log('this.path: ', this);
        if (!/^[0-9a-zA-Z_ ]+$/.test(currentField)) {
          return this.createError({
            path: this.path,
            message: `Invalid characters in the header name: ${currentField}. Accepted characters: alphabet, numbers, underscore(_) and spaces.`,
          });
        } else {
          return true;
        }
      },
    ),
  start: yup.number().label('Start').required().min(1).max(1000),
  length: yup.number().label('Field Length').required().min(1).max(1000),
  clientHeaders: yup.string().label('Client Header').notRequired(),
});

const getField = (
  input: string | InputLayoutModel,
  nameExtractorFn: (field: InputLayoutModel) => string = headerNameExtractorFn,
): string => (typeof input === 'string' ? input : nameExtractorFn(input));

const getFields = (
  input: (string | InputLayoutModel)[],
  nameExtractorFn: (field: InputLayoutModel) => string = headerNameExtractorFn,
): string[] => input.map((a) => getField(a, nameExtractorFn));

const inputFieldsConsumerAddressCombinationsValidator = yup
  .array<string | InputLayoutModel>()
  .test({
    name: 'ConsumerAddressCombinations',
    test(fields: (string | InputLayoutModel)[]) {
      const { path, createError } = this;
      const fieldNamesWithIndex = getFields(fields).map((a, index) => ({
        index,
        fieldName: a,
      }));

      const fieldNames = fieldNamesWithIndex
        .map((a) => a.fieldName)
        .filter((a) => a !== null && a !== undefined);

      if (fieldNamesWithIndex.length === 0) {
        return true;
      } else {
        const errors: yup.ValidationError[] = [];
        fieldNamesWithIndex.forEach(({ fieldName, index }) => {
          const combinations = consumerAddressCombinations.find(
            (a) => a.fieldName.toLowerCase() === fieldName.toLowerCase(),
          );
          // const shouldNotHaveFields: string[] = [];
          if (combinations) {
            const shouldNotHaveFields = fieldNames.filter((a) =>
              combinations.shouldNotExistList.includes(a.toLowerCase()),
            );
            if (shouldNotHaveFields.length > 0) {
              errors.push(
                createError({
                  path: `${path}[${index}].name`,
                  message: `Cannot select the fields ${shouldNotHaveFields.join(
                    ', ',
                  )} when the field ${fieldNames[index]} is selected`,
                }),
              );
            }
          }
        });
        return errors.length === 0
          ? true
          : this.createError({
              path: `${this.path}Errors.addressCombinations`,
            });
      }
    },
  });

const inputFieldsConsumerAddressCompletenessValidator = yup
  .array<string | InputLayoutModel>()
  .test({
    name: 'ConsumerAddressCompleteness',
    test: async (inputFields: (string | InputLayoutModel)[]) => {
      const fields = getFields(inputFields);
      const response = await checkAddressCompleteness(fields, addressValidator);
      return response.length === 0;
    },
  });

const inputFieldsConsumerNameCombinationsValidator = yup
  .array<string | InputLayoutModel>()
  .test({
    name: 'ConsumerNameCombinations',
    test(fields: (string | InputLayoutModel)[]) {
      const { path, createError } = this;
      const fieldNamesWithIndex = getFields(fields).map((a, index) => ({
        index,
        fieldName: a,
      }));

      const fieldNames = fieldNamesWithIndex
        .map((a) => a.fieldName)
        .filter((a) => a !== null && a !== undefined);
      // console.log('ConsumerNameCombinations: ', fieldNames);
      if (fieldNamesWithIndex.length === 0) {
        return true;
      } else {
        const errors: yup.ValidationError[] = [];
        fieldNamesWithIndex.forEach(({ fieldName, index }) => {
          const combinations = consumerNameCombinations.find(
            (a) => a.fieldName.toLowerCase() === fieldName.toLowerCase(),
          );
          // const shouldNotHaveFields: string[] = [];
          if (combinations) {
            const shouldNotHaveFields = fieldNames.filter((a) =>
              combinations.shouldNotExistList.includes(a.toLowerCase()),
            );
            if (shouldNotHaveFields.length > 0) {
              errors.push(
                createError({
                  path: `${path}[${index}].name`,
                  message: `Cannot select the fields ${shouldNotHaveFields.join(
                    ', ',
                  )} when the field ${fieldNames[index]} is selected`,
                }),
              );
            }
          }
        });

        const toReturn =
          errors.length === 0
            ? true
            : this.createError({
                path: `${this.path}Errors.nameCombinations`,
              });
        // console.log('NameCombinations: ', errors, toReturn);
        return toReturn;
      }
    },
  });

const headerNameExtractorFn = (field: InputLayoutModel): string =>
  field.fieldName || '';

const inputFieldsConsumerAddressMinimumsValidator = yup
  .array<string | InputLayoutModel>()
  .test({
    name: 'ConsumerAddressMinimums',
    test(fields: (string | InputLayoutModel)[]) {
      const { path, createError } = this;
      const effectiveFields = getFields(fields, headerNameExtractorFn);
      // console.log('In consumerAddressMinimums: ', effectiveFields);
      for (const fieldsSet of consumerAddressMinimumFields) {
        const localValid = fieldsSet.every((b) =>
          effectiveFields.some((c) => {
            const toReturn = c?.toLowerCase() === b.toLowerCase();
            return toReturn;
          }),
        );
        if (localValid) {
          return true;
        }
      }
      // console.log(`In consumerAddressMinimums soo:  ${path}Errors`);
      const errorMessage = `At least one of the field combinations below needs to be selected\\n${consumerAddressMinimumFields
        .map((a) => a.join(', '))
        .join('\\n')}`;
      return createError({
        path: `${path}Errors.addressMinimums`,
        message: errorMessage,
      });
    },
  });

const inputFieldsConsumerNameMinimumsValidator = yup
  .array<string | InputLayoutModel>()
  .test({
    name: 'ConsumerNameMinimums',
    test(fields: (string | InputLayoutModel)[]) {
      const { path, createError } = this;

      const effectiveFields = getFields(fields, headerNameExtractorFn);

      for (const fieldsSet of consumerNameMinimumsFields) {
        const localValid = fieldsSet.every((b) =>
          effectiveFields.some((c) => {
            const toReturn = c?.toLowerCase() === b.toLowerCase();
            return toReturn;
          }),
        );
        if (localValid) {
          return true;
        }
      }
      // console.log('In consumerNameMinimums: ', effectiveFields);

      const errorMessage = `At least one of the field combinations below needs to be selected\\n${consumerNameMinimumsFields
        .map((a) => a.join(', '))
        .join('\\n')}`;
      return createError({
        path: `${path}Errors.nameMinimums`,
        message: errorMessage,
      });
    },
  });

const addressValidator = yup
  .array<string | InputLayoutModel>()
  .default([])
  .transform((values) => values || [])
  .concat(inputFieldsConsumerAddressMinimumsValidator)
  .concat(inputFieldsConsumerAddressCombinationsValidator);

export const inputFieldsCombinationsValidator = yup
  .array<string | InputLayoutModel>()
  .default([])
  .transform((values) => {
    return values || [];
  })
  .concat(addressValidator)
  .concat(inputFieldsConsumerNameMinimumsValidator)
  .concat(inputFieldsConsumerNameCombinationsValidator)
  .concat(inputFieldsConsumerAddressCompletenessValidator);
// .required('BBBB');
// .min(1);

export const inputFieldsValidator: yup.NotRequiredArraySchema<InputLayoutModel> =
  yup
    .array<InputLayoutModel>(inputFieldValidator.required())
    .default([])
    .transform((values) => {
      return values || [];
    })
    // .required('AAA')
    // .min(1, 'Input Fields are required')
    .test(
      'unique',
      'Field Headers must be unique.',
      function validateUnique(currentFields: InputLayoutModel[]) {
        // console.log('currentFields: ', currentFields);
        const effectiveFields = currentFields.map((a) => a.fieldName || '');
        if (effectiveFields.length < 1) {
          return true;
        }
        // console.log(this.parent.filter, this.parent, this);
        const grouped = collectionUtils.groupBy(
          currentFields,
          (a) => (a.fieldName || '').toLowerCase(),
          (a) => a.fieldName || '',
        );
        const sameFields = Object.values(grouped)
          .filter((a) => a.length > 1 && a[0] !== '')
          .map((a) => a[0]);
        const sameFieldNames = sameFields.join(', ');
        return sameFields.length > 0
          ? this.createError({
              path: `${this.path}`,
              message: `Field Headers must be unique. ${sameFieldNames} occur more than once.`,
            })
          : true;
      },
    )
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .concat(inputFieldsCombinationsValidator as any);
